import { useMemo } from 'react';

import dayjs from 'dayjs';
import pluralize from 'pluralize';

import { DATE_TIME_WITH_OFFSET } from '@cast/constants';
import {
  ChartTooltipPayload,
  Column,
  DataPopper,
  DataPopperRow,
  DataTable,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';

import {
  OomKillBucketQueryRange,
  useOomKillBucketWorkloadsQuery,
} from './_hooks/useOomKillBucketWorkloadsQuery';
import { OomKillBucketWorkload, OOMKillChartItem } from './types';
import { oomKillsColorMap } from './utils';

const PREVIEW_LIMIT = 5;

const SKELETON_ROWS: OomKillBucketWorkload[] = [
  {
    workloadName: '',
    workloadType: '',
    namespace: '',
    container: '',
    eventCount: '',
    timestamp: '',
  },
  {
    workloadName: '',
    workloadType: '',
    namespace: '',
    container: '',
    eventCount: '',
    timestamp: '',
  },
];

type OomKillBucketPreviewArgs = {
  bucketTimestamp: string;
  managed: boolean;
};

type OomKillTooltipProps = {
  payload?: ChartTooltipPayload<OOMKillChartItem>[];
  timeZone: string;
  /**
   * Breakdown table (workload / container / timestamp). Cluster dashboard also
   * shows "Click bubble to view all" when `clickable` is true (default).
   */
  interactive?: boolean;
  /**
   * When interactive: show the drawer CTA. Set false on per-workload charts
   * that only show the breakdown on hover.
   */
  clickable?: boolean;
  rangeSelection?: OomKillBucketQueryRange;
  /**
   * When set, rows come from this resolver (workload-scoped series) instead of
   * the cluster bucketTimestamp query.
   */
  getPreviewWorkloads?: (
    args: OomKillBucketPreviewArgs
  ) => OomKillBucketWorkload[];
};

const useOomKillTooltipEvent = (
  payload?: ChartTooltipPayload<OOMKillChartItem>[]
) =>
  useMemo(() => {
    return payload?.find(
      (point) =>
        Object.keys(oomKillsColorMap).includes(String(point.dataKey)) &&
        point.payload[point.dataKey as keyof OOMKillChartItem] !== null
    );
  }, [payload]);

const OomKillSummaryTooltip = ({
  payload,
  timeZone,
}: Omit<OomKillTooltipProps, 'interactive'>) => {
  const event = useOomKillTooltipEvent(payload);

  if (!payload?.length || !event) {
    return null;
  }

  const timestamp = dayjs
    .unix(event?.payload.chartTimestampUnix ?? 0)
    .tz(timeZone);

  const day = timestamp.format('MMM DD');
  const time = timestamp.format('h:mm A');

  return (
    <DataPopper size="small" sx={{ width: 231 }}>
      <DataPopperRow
        dividerBottom
        left={
          <Typography
            variant="L10M"
            color={tokens.theme.fg.structural.secondary}
          >
            {day}
          </Typography>
        }
        right={
          <Typography
            variant="L10M"
            color={tokens.theme.fg.structural.secondary}
          >
            {time}
          </Typography>
        }
      />
      <DataPopperRow
        indicator={
          oomKillsColorMap[event.dataKey as keyof typeof oomKillsColorMap]
        }
        left={
          <Typography variant="P10R" component="span">
            OOM Kill
          </Typography>
        }
        right={
          <Stack direction="row" gap={4}>
            <Typography variant="P10R">
              {event.payload[event.dataKey as keyof OOMKillChartItem]}
            </Typography>
          </Stack>
        }
      />
    </DataPopper>
  );
};

const OomKillBreakdownTooltip = ({
  payload,
  timeZone,
  rangeSelection,
  clickable = true,
  getPreviewWorkloads,
}: Omit<OomKillTooltipProps, 'interactive'>) => {
  const event = useOomKillTooltipEvent(payload);

  const dataKey = event?.dataKey as keyof typeof oomKillsColorMap | undefined;
  const managed = dataKey === 'managedCount';
  const bucketTimestamp = event?.payload.timestamp;

  const useClusterBucketQuery = !getPreviewWorkloads;

  const { workloads: clusterWorkloads, isLoading: isClusterLoading } =
    useOomKillBucketWorkloadsQuery({
      bucketTimestamp,
      managed,
      enabled: useClusterBucketQuery && !!event && !!bucketTimestamp,
      rangeSelection,
    });

  const workloads = useMemo(() => {
    if (!getPreviewWorkloads || !bucketTimestamp) {
      return clusterWorkloads;
    }
    return getPreviewWorkloads({ bucketTimestamp, managed });
  }, [getPreviewWorkloads, bucketTimestamp, managed, clusterWorkloads]);

  const isLoading = useClusterBucketQuery ? isClusterLoading : false;

  if (!payload?.length || !event || !dataKey) {
    return null;
  }

  const preview = isLoading ? SKELETON_ROWS : workloads.slice(0, PREVIEW_LIMIT);
  const remaining = isLoading ? 0 : workloads.length - preview.length;

  return (
    <Stack
      borderRadius="4px"
      bgcolor={tokens.theme.bg.structural.surface}
      width={430}
      overflow="hidden"
      // Let bubble clicks pass through the hover tooltip to the chart.
      sx={{ pointerEvents: 'none' }}
      data-testid="oom-kill-tooltip"
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        bgcolor={tokens.theme.data.red500}
        alignItems="center"
        px={12}
        py={4}
      >
        <Typography
          variant="L10B"
          color={tokens.theme.fg.structural.primaryOnInverse}
          data-testid="oom-kill-tooltip-title"
        >
          {event.value} - OOMKill
        </Typography>
        {clickable && (
          <Typography
            variant="P10R"
            color={tokens.theme.fg.structural.primaryOnInverse}
          >
            Click bubble to view all
          </Typography>
        )}
      </Stack>
      <Stack
        gap={4}
        border={tokens.theme.border.structural.neutralSubtle}
        borderTop="none"
      >
        <DataTable sizing="s" data={preview} testId="oom-kill-tooltip-table">
          <Column<OomKillBucketWorkload>
            headerCellSx={{ typography: 'L10M' }}
            id="workloadName"
            minWidth={120}
            header="Workload"
            isLoading={isLoading}
            renderCell={({ workloadName, namespace }) => (
              <Stack>
                <Typography variant="P10B" noWrap>
                  {workloadName}
                </Typography>
                <Typography
                  variant="P10R"
                  color={tokens.theme.fg.structural.secondary}
                  noWrap
                >
                  {namespace}
                </Typography>
              </Stack>
            )}
          />
          <Column<OomKillBucketWorkload>
            headerCellSx={{ typography: 'L10M' }}
            id="container"
            minWidth={140}
            header="Container"
            isLoading={isLoading}
            renderCell={({ container }) => (
              <Typography variant="P10R" noWrap>
                {container}
              </Typography>
            )}
          />
          <Column<OomKillBucketWorkload>
            headerCellSx={{ typography: 'L10M' }}
            id="timestamp"
            minWidth={150}
            header="Timestamp"
            isLoading={isLoading}
            renderCell={({ timestamp }) => (
              <Typography
                variant="P10R"
                color={tokens.theme.fg.structural.secondary}
                noWrap
              >
                {dayjs(timestamp).tz(timeZone).format(DATE_TIME_WITH_OFFSET)}
              </Typography>
            )}
          />
        </DataTable>
        {remaining > 0 && (
          <Typography variant="P10M" px={12} pb={8}>
            + {remaining} more {pluralize('event', remaining)}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

export const OomKillTooltip = ({
  payload,
  timeZone,
  interactive = false,
  clickable = true,
  rangeSelection,
  getPreviewWorkloads,
}: OomKillTooltipProps) => {
  if (interactive) {
    return (
      <OomKillBreakdownTooltip
        payload={payload}
        timeZone={timeZone}
        rangeSelection={rangeSelection}
        clickable={clickable}
        getPreviewWorkloads={getPreviewWorkloads}
      />
    );
  }

  return <OomKillSummaryTooltip payload={payload} timeZone={timeZone} />;
};
